import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { catalogAPI, agentAPI, contractRequestAPI } from '@/lib/api';
import { isKRRole, isAgent, getErrorMessage } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  FileText, Loader2, Search, Send, ChevronRight
} from 'lucide-react';

const REQUEST_TYPES = [
  { value: 'New_Contract', label: 'New Contract' },
  { value: 'Level_Change', label: 'Level Change' },
  { value: 'Transfer', label: 'Transfer' },
  { value: 'Reinstatement', label: 'Reinstatement' },
  { value: 'Termination', label: 'Termination' },
  { value: 'Other', label: 'Other' },
];

const PRODUCTS = ['ACA', 'Medicare', 'Life_Annuity', 'Ancillary', 'Private_Health_Plans'];

export default function RequestContractPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isKR = isKRRole(user);

  const [catalog, setCatalog] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [agentNpn, setAgentNpn] = useState(searchParams.get('npn') || '');
  const [agentSearch, setAgentSearch] = useState('');
  const [requestType, setRequestType] = useState('New_Contract');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [catalogRes, agentsRes] = await Promise.all([
        catalogAPI.flatCatalog(),
        agentAPI.list({ limit: 5000 }),
      ]);
      setCatalog(catalogRes.data.catalog || []);
      const agentData = agentsRes.data;
      setAgents(Array.isArray(agentData) ? agentData : agentData.agents || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  // Filtered catalog based on selections
  const availableProducts = useMemo(() => {
    const products = new Set(catalog.map(c => c.product));
    return PRODUCTS.filter(p => products.has(p));
  }, [catalog]);

  const filteredCarriers = useMemo(() => {
    let items = catalog;
    if (selectedProduct) {
      items = items.filter(c => c.product === selectedProduct);
    }
    const carriers = new Map();
    items.forEach(c => {
      if (!carriers.has(c.carrier_id)) {
        carriers.set(c.carrier_id, { carrier_id: c.carrier_id, carrier_name: c.carrier_name });
      }
    });
    return Array.from(carriers.values()).sort((a, b) => a.carrier_name.localeCompare(b.carrier_name));
  }, [catalog, selectedProduct]);

  const filteredLevels = useMemo(() => {
    let items = catalog;
    if (selectedProduct) items = items.filter(c => c.product === selectedProduct);
    if (selectedCarrier) items = items.filter(c => c.carrier_id === selectedCarrier);
    const levels = new Map();
    items.forEach(c => {
      if (c.level_id && !levels.has(c.level_id)) {
        levels.set(c.level_id, { level_id: c.level_id, level_name: c.level_name, contract_type_id: c.contract_type_id });
      }
    });
    return Array.from(levels.values());
  }, [catalog, selectedProduct, selectedCarrier]);

  const selectedCatalogItem = useMemo(() => {
    if (!selectedCarrier || !selectedLevel) return null;
    return catalog.find(c =>
      c.carrier_id === selectedCarrier &&
      c.level_id === selectedLevel &&
      (!selectedProduct || c.product === selectedProduct)
    );
  }, [catalog, selectedCarrier, selectedLevel, selectedProduct]);

  // Agent search
  const filteredAgents = useMemo(() => {
    if (!agentSearch) return agents.slice(0, 20);
    const q = agentSearch.toLowerCase();
    return agents.filter(a =>
      (a.first_name + ' ' + a.last_name).toLowerCase().includes(q) ||
      a.npn?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [agents, agentSearch]);

  const selectedAgent = agents.find(a => a.npn === agentNpn);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agentNpn || !selectedCarrier || !selectedLevel) {
      toast.error('Please select an agent, carrier, and level');
      return;
    }

    setSubmitting(true);
    try {
      await contractRequestAPI.create({
        npn: agentNpn,
        carrier_id: selectedCarrier,
        contract_type_id: selectedCatalogItem?.contract_type_id || '',
        requested_level_id: selectedLevel,
        request_type: requestType,
        request_notes: notes || null,
      });
      toast.success('Contract request submitted successfully');
      navigate('/requests');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to submit request'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="request-contract-page">
      <PageHeader
        title="Request Contract"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Requests', href: '/requests' },
          { label: 'New Request' },
        ]}
      />

      <div className="page-content max-w-3xl mx-auto">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <FileText size={18} className="text-sky-500" />
                New Contract Request
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Request Type */}
              <div>
                <Label>Request Type</Label>
                <Select value={requestType} onValueChange={setRequestType}>
                  <SelectTrigger data-testid="request-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REQUEST_TYPES.map(rt => (
                      <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Agent Selection */}
              <div>
                <Label>Agent</Label>
                {selectedAgent ? (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20">
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {selectedAgent.first_name} {selectedAgent.last_name}
                      </p>
                      <p className="text-xs text-slate-500">NPN: {selectedAgent.npn}</p>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setAgentNpn('')} data-testid="change-agent-btn">
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={agentSearch}
                        onChange={(e) => setAgentSearch(e.target.value)}
                        placeholder="Search by name, NPN, or email..."
                        className="pl-9"
                        data-testid="agent-search-input"
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                      {filteredAgents.map(agent => (
                        <button
                          key={agent.npn}
                          type="button"
                          onClick={() => { setAgentNpn(agent.npn); setAgentSearch(''); }}
                          className="w-full flex items-center justify-between p-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                          data-testid={`agent-option-${agent.npn}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{agent.first_name} {agent.last_name}</p>
                            <p className="text-[10px] text-slate-500">{agent.npn} | {agent.email}</p>
                          </div>
                          <ChevronRight size={14} className="text-slate-300" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Product (Controlled Enum) */}
              <div>
                <Label>Product</Label>
                <Select value={selectedProduct} onValueChange={(v) => { setSelectedProduct(v); setSelectedCarrier(''); setSelectedLevel(''); }}>
                  <SelectTrigger data-testid="product-select">
                    <SelectValue placeholder="Select product line..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProducts.map(p => (
                      <SelectItem key={p} value={p}>{p.replace(/_/g, '/')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Carrier */}
              <div>
                <Label>Carrier</Label>
                <Select value={selectedCarrier} onValueChange={(v) => { setSelectedCarrier(v); setSelectedLevel(''); }}>
                  <SelectTrigger data-testid="carrier-select">
                    <SelectValue placeholder="Select carrier..." />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCarriers.map(c => (
                      <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Level */}
              <div>
                <Label>Level</Label>
                <Select value={selectedLevel} onValueChange={setSelectedLevel} disabled={!selectedCarrier}>
                  <SelectTrigger data-testid="level-select">
                    <SelectValue placeholder={selectedCarrier ? 'Select level...' : 'Select carrier first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredLevels.map(l => (
                      <SelectItem key={l.level_id} value={l.level_id}>{l.level_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selected Summary */}
              {selectedCatalogItem && (
                <div className="p-3 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800" data-testid="selection-summary">
                  <p className="text-xs font-medium text-sky-700 dark:text-sky-400 mb-1">Selected Contract</p>
                  <p className="text-sm text-slate-900 dark:text-slate-100">
                    {selectedCatalogItem.carrier_name} — {selectedCatalogItem.product.replace(/_/g, '/')} — {selectedCatalogItem.level_name}
                  </p>
                </div>
              )}

              {/* Notes */}
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes for this request..."
                  className="min-h-[80px]"
                  data-testid="request-notes-input"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={submitting || !agentNpn || !selectedCarrier || !selectedLevel}
                className="w-full"
                data-testid="submit-request-btn"
              >
                {submitting ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : (
                  <Send size={16} className="mr-2" />
                )}
                Submit Contract Request
              </Button>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}
